import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, first, tap} from 'rxjs/operators';
import { of } from 'rxjs';
import * as ReceivingsActions from '../actions/receivings.actions';
import { ReceivingsService } from '@app/services/receivings/receiving.service';
import { Store } from '@ngrx/store';
import { CategoriesService } from '@app/services/categories/categories.service';
import { AreasService } from '@app/services/areas/areas.service';
import { selectReceivingsDirectionState, selectReceivingsSortState } from '../selectors/receivings.selectors';
import { ToastrService } from 'ngx-toastr';
import * as CoreActions from '@app/core/store/core.action';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';

@Injectable()
export class ReceivingsEffects {

  loadReceivings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReceivingsActions.loadReceivings),
      switchMap(action => {
        return this._receivings.getReceivings('created_at', 'desc', action.query,0,100).pipe(
          map((data): any => {
            if(data.result!=null && data.result!='undefined' && data.result=='failed.access' && this.location.path()=='/receivings'){
              this.store$.dispatch(CoreActions.showPermissionMessage({
                title: this._translation.instant('errormessage.warning'),
                body: this._translation.instant('errormessage.access')
              }));
            }

            return ReceivingsActions.loadReceivingsSuccess({ receivings: data.receivings, total: data.total })
          }),
          catchError(error => {
            return of(ReceivingsActions.loadReceivingsFailure({ error }))
          })
        )
      })
    );
  });

  searchReceivings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReceivingsActions.search),
      switchMap(action => {
        let direction: string;
        let sort: string;
        // this.store$.select(selectReceivingsDirectionState).pipe(first()).subscribe(res => direction = res);
        // this.store$.select(selectReceivingsSortState).pipe(first()).subscribe(res => sort = res);

        return this._receivings.searchReceivings('product', 'ASC', action.query).pipe(
          map((data): any => {
            return ReceivingsActions.searchSuccess({ receivings: data.receivings, total: data.total })
          }),
          catchError(error => {
            return of(ReceivingsActions.searchFailure({ error }))
          })
        )
      })
    );
  });

  createReceiving$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReceivingsActions.saveReceiving),
      switchMap(action => {
        return this._receivings.onSave(action.form).pipe(
          map((response): any => {
            if(response.result!='undefined' && response.result=='failed.max'){
              this.store$.dispatch(CoreActions.showErrorMessage({
                title: this._translation.instant('errormessage.warning'),
                body: this._translation.instant('errormessage.limitreceiving'),
                show_upgrade: true
              }));
              return of(ReceivingsActions.saveReceivingFailure({ error: 'An error occurred.' }))
            }

            if(response.error && response.message!=''){
              this._toastr.error(this._translation.instant(response.message));
              return ReceivingsActions.saveReceivingFailure({ error: 'An error occurred.' })
            }
            else{
              return ReceivingsActions.saveReceivingSuccess(response)
            }
            
          }),
          catchError(error => {
            return of(ReceivingsActions.saveReceivingFailure({ error }))
          })
        )
      })
    );
  });

  updateReceiving$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReceivingsActions.updateReceiving),
      switchMap(action => {
        return this._receivings.updateReceiving(action.form).pipe(
          map((response): any => {
            if(response.error && response.message!=''){
              this._toastr.error(this._translation.instant(response.message));
              return ReceivingsActions.updateReceivingFailure({ error: 'An error occurred.' })
            }

            return ReceivingsActions.updateReceivingSuccess(response)
          }),
          catchError(error => {
            return of(ReceivingsActions.updateReceivingFailure({ error }))
          })
        )
      })
    );
  });

  loadReceiving$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReceivingsActions.loadReceiving),
      switchMap(action => {
        return this._receivings.getReceiving(action.productMovementHistoryID).pipe(
          map(({ receiving }): any => {
            return ReceivingsActions.loadReceivingSuccess({ receiving })
          }),
          catchError(error => {
            return of(ReceivingsActions.loadReceivingFailure({ error }))
          })
        )
      })
    );
  });

  deleteReceivings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReceivingsActions.deleteReceivings),
      switchMap(action => {
        return this._receivings.batchDelete(action.selected, action.reason).pipe(
          map(() => {
            return ReceivingsActions.deleteReceivingsSuccess(action.query);}),
          catchError(error => {
            return of(ReceivingsActions.deleteReceivingsFailure({ error }))
          })
          )
      })
    );
  });

  refreshReceivingsAfterDelete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReceivingsActions.deleteReceivingsSuccess),
      switchMap(action => {
        return of(0).pipe(
          map(() => ReceivingsActions.loadReceivings({query:''})),
          catchError(error => of(ReceivingsActions.deleteReceivingsFailure({ error }))
          ))
      })
    );
  });

  cleanReceivingsAfterDelete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReceivingsActions.deleteReceivingsSuccess),
      switchMap(action => {
        return of(0).pipe(
          map(() => ReceivingsActions.setSelected({ selected: [] })),
          catchError(error => of(ReceivingsActions.deleteReceivingsFailure({ error }))
          ))
      })
    );
  });

  deleteReceiving$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReceivingsActions.deleteReceiving),
      switchMap(action => {
        return this._receivings.onDelete(action.productMovementHistoryID, action.reason).pipe(
          tap(() => {
            // this._toastr.error(`Successfully deleted receiving.`);
          }),
          map((): any => {
            return ReceivingsActions.deleteReceivingSuccess({ productMovementHistoryID: action.productMovementHistoryID })
          }),
          catchError(error => {
            return of(ReceivingsActions.deleteReceivingFailure({ error: error.message }))
          })
        )
      })
    );
  });

  setSort$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReceivingsActions.setSort),
      switchMap(action => {
        return this._receivings.getReceivings(action.sort, action.direction, action.query,0,100).pipe(
          map((data): any => {
            return ReceivingsActions.loadReceivingsSuccess({ receivings: data.receivings, total: data.total })
          })
        )
      })
    );
  });

  constructor(
    private actions$: Actions, private _receivings: ReceivingsService,
    private store$: Store, private _categories: CategoriesService,
    private _areas: AreasService,
    private _toastr: ToastrService,
    public _router: Router,
    private location : Location,
    public _translation: TranslateService
  ) { }

}
